/**
 * El buscador del panel, en la barra de arriba.
 *
 * Nació dentro del globo de la mascota y se mudó aquí, junto a la campana, que
 * es donde la gente lo busca. En la primera pantalla sólo estaba a mano estando
 * en la primera pantalla — y el momento en que uno no encuentra algo es casi
 * siempre estando en otra.
 *
 * Busca por cómo lo llama la gente, no por cómo se llama en el menú: «pagos»
 * lleva a Cobros y «facturas» a Facturación. Ver `dashboardSearch`.
 *
 * ── Dos fuentes, un desplegable ─────────────────────────────────────────────
 * Los DESTINOS —las veinticuatro secciones— salen al instante: son una lista
 * fija que vive en el panel. El CONTENIDO —contactos, publicaciones, ofertas,
 * páginas— lo trae el servidor, porque sólo él sabe qué hay dentro de este
 * proyecto y son demasiados para tenerlos aquí.
 *
 * Los destinos van primero y no por orden alfabético: quien escribe «cont» casi
 * siempre quiere ir a Contactos, no abrir un contacto llamado así. Lo de abajo
 * es lo que no cabría en ningún menú.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { IconArrowRight, IconDatabase, IconFileText, IconLayoutDashboard, IconReceipt, IconSearch, IconUser, IconX } from '@tabler/icons-react'
import { SEARCH_GROUPS, projectContentEntries, searchDashboard, type SearchEntry, type SearchKind, type SearchTarget } from '../lib/dashboardSearch'
import { searchProjectContent } from '../lib/api'
import { useI18n } from '../lib/i18n'
import { useSearchPlaceholder } from '../hooks/useSearchPlaceholder'

/**
 * El icono de cada clase de resultado.
 *
 * Es la mitad de la diferencia: el rótulo del grupo la dice una vez arriba y el
 * icono la repite en cada fila, que es lo que se mira cuando ya se está
 * recorriendo la lista con las flechas.
 */
const KIND_ICONS: Record<SearchKind, typeof IconFileText> = {
  destination: IconLayoutDashboard,
  page: IconFileText,
  cms: IconDatabase,
  contact: IconUser,
  offer: IconReceipt,
}

/** Lo que se espera a que pare de escribir antes de preguntarle al servidor. */
const DEBOUNCE_MS = 220

export function DashboardSearch({ onGo, token, projectId }: {
  onGo: (target: SearchTarget) => void
  token: string
  /** Sin proyecto no hay contenido que buscar: quedan sólo los destinos. */
  projectId?: string | undefined
}) {
  const { t, language } = useI18n()
  const searchPlaceholder = useSearchPlaceholder('search.placeholder')

  const [query, setQuery] = useState('')
  const [highlight, setHighlight] = useState(0)
  const [content, setContent] = useState<SearchEntry[]>([])
  const boxRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const destinations = useMemo(() => searchDashboard(query, t, language), [query, t, language])

  /* Lo escrito cambia, la selección vuelve al primero: dejarla donde estaba
     apunta a un resultado que ya no es el que se está mirando. */
  useEffect(() => { setHighlight(0) }, [query])

  /* El contenido del proyecto, con un respiro entre teclas. Sin la espera, cada
     letra dispara una consulta y las respuestas se pisan entre ellas. */
  useEffect(() => {
    const needle = query.trim()
    if (!projectId || needle.length < 2) { setContent([]); return }
    let alive = true
    const timer = window.setTimeout(() => {
      void searchProjectContent(token, projectId, needle)
        .then((found) => {
          if (!alive) return
          const entries = projectContentEntries(found, t)
          setContent(entries)
        })
        .catch(() => { if (alive) setContent([]) })
    }, DEBOUNCE_MS)
    return () => { alive = false; window.clearTimeout(timer) }
    /* `t` entra en la lista porque los rótulos de respaldo —«Contacto», «Sin
       título»— se escriben aquí y quedan congelados en el resultado: sin esto,
       cambiar de idioma dejaba la lista anterior en el idioma anterior. */
  }, [query, token, projectId, t])

  /* Los destinos primero y el contenido detrás, en una sola lista: las flechas
     recorren todo seguido, que es como se espera que funcione un desplegable. */
  const results = useMemo(() => [...destinations, ...content], [destinations, content])

  /**
   * La misma lista, partida por clases y en el orden de `SEARCH_GROUPS`.
   *
   * Se guarda el índice que cada fila tiene en la lista PLANA: los rótulos no se
   * recorren con las flechas, así que la selección sigue contando filas y no
   * posiciones dentro de su grupo.
   */
  const groups = useMemo(() => SEARCH_GROUPS
    .map((group) => ({
      kind: group.kind,
      label: t(group.labelKey),
      rows: results
        .map((entry, index) => ({ entry, index }))
        .filter((row) => row.entry.kind === group.kind),
    }))
    .filter((group) => group.rows.length > 0), [results, t])

  const open = query.trim().length >= 2

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: PointerEvent) => {
      if (!boxRef.current?.contains(event.target as Node)) setQuery('')
    }
    document.addEventListener('pointerdown', onPointerDown)
    return () => document.removeEventListener('pointerdown', onPointerDown)
  }, [open])

  /* El atajo de siempre para un buscador. Con `/` a secas, escribir una barra en
     cualquier campo del panel robaría el foco. */
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!(event.key === 'k' && (event.metaKey || event.ctrlKey))) return
      event.preventDefault()
      inputRef.current?.focus()
      inputRef.current?.select()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [])

  const go = (target: SearchTarget) => {
    setQuery('')
    inputRef.current?.blur()
    onGo(target)
  }

  /* Vaciar y soltar el foco: las dos cosas, porque en estrecho lo que repliega
     el buscador de vuelta a su lupa es justo eso —ni texto ni foco—. Con una
     sola se quedaría abierto y ocupando la barra entera. */
  const close = () => {
    setQuery('')
    inputRef.current?.blur()
  }

  return (
    /* Por dentro, el MISMO buscador que la barra de Páginas: sus clases, su
       cápsula y su icono. Es el buscador que ya conoce quien usa el panel, y
       hacer otro con el mismo trabajo habría sido un segundo estilo de campo que
       mantener en paralelo. Lo único propio es la caja blanca que lo envuelve,
       que es lo que lo separa del fondo de la barra. */
    /* `is-active` mientras haya algo escrito, y no sólo el `:focus-within` de la
       hoja de estilos. En estrecho el buscador se pliega en un botón, y el foco
       se va ANTES de que un clic llegue a su destino: al apoyar el dedo en un
       resultado el campo se desenfocaba, la caja volvía a su tamaño de botón y
       la fila que se acababa de pulsar ya no estaba ahí cuando tocaba repartir
       el clic — así que no se iba a ninguna parte y la lista quedaba encendida y
       espachurrada. Con texto dentro sigue abierta pase lo que pase con el foco;
       la cierran `go`, Escape y el clic fuera, que vacían la consulta. */
    <div className={`dash-topbar-search${query ? ' is-active' : ''}`} ref={boxRef}>
      <div className="dash-toolbar-search">
        <button
          type="button"
          className="dash-toolbar-search-icon"
          aria-label={t('search.aria')}
          tabIndex={-1}
          onClick={() => inputRef.current?.focus()}
        >
          <IconSearch size={16} />
        </button>
        <input
          ref={inputRef}
          value={query}
          type="search"
          placeholder={searchPlaceholder}
          aria-label={t('search.aria')}
          autoComplete="off"
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Escape') { close(); return }
            if (results.length === 0) return
            if (event.key === 'ArrowDown') {
              event.preventDefault()
              setHighlight((current) => (current + 1) % results.length)
            } else if (event.key === 'ArrowUp') {
              event.preventDefault()
              setHighlight((current) => (current - 1 + results.length) % results.length)
            } else if (event.key === 'Enter') {
              event.preventDefault()
              const entry = results[highlight] ?? results[0]
              if (entry) go(entry.target)
            }
          }}
        />
        {/* La pista del atajo se va al escribir: con texto dentro compite con lo
            que se está tecleando, y a esa altura ya nadie necesita el
            recordatorio. */}
        {query.length === 0 ? <kbd className="dash-topbar-search-kbd">⌘K</kbd> : null}
        {/* La salida. En un teléfono no hay Escape, y abierto el campo se queda
            con casi toda la barra, así que apenas queda sitio libre donde pulsar
            para cerrarlo: sin esta equis se entra a buscar y no se sale.

            Siempre en el árbol y escondida con CSS, no con una condición aquí:
            en estrecho tiene que verse también con el campo VACÍO, que es el
            primer instante después de pulsar la lupa y justo cuando alguien se
            arrepiente, y ese estado —desplegado— sólo lo conoce la hoja de
            estilos.

            El trabajo va en `pointerdown` y no sólo en el clic: al apoyar el
            dedo, el campo pierde el foco, el buscador se repliega a 32px y la
            equis ya no está donde se pulsó cuando toca repartir el clic.
            `preventDefault` retiene el foco hasta que `close` lo suelta a
            propósito; `onClick` se queda para el teclado. */}
        <button
          type="button"
          className="dash-toolbar-search-clear dash-topbar-search-close"
          aria-label={t('search.close')}
          onPointerDown={(event) => { event.preventDefault(); close() }}
          onClick={close}
        >
          <IconX size={13} />
        </button>
      </div>

      {open ? (
        <div className="dash-mascot-search-results" role="listbox" aria-label={t('search.resultsAria')}>
          {results.length === 0 ? (
            <p className="dash-mascot-search-empty">{t('search.empty', { query: query.trim() })}</p>
          ) : groups.map((group) => (
            <div className="dash-search-group" key={group.kind} role="group" aria-label={group.label}>
              <p className="dash-search-group-label">{group.label}</p>
              {group.rows.map(({ entry, index }) => {
                const Icon = KIND_ICONS[entry.kind]
                return (
                  <button
                    key={`${entry.kind}-${entry.label}-${index}`}
                    type="button"
                    role="option"
                    aria-selected={index === highlight}
                    className={`dash-mascot-search-hit${index === highlight ? ' is-active' : ''}`}
                    onPointerEnter={() => setHighlight(index)}
                    onClick={() => go(entry.target)}
                  >
                    <Icon className="dash-search-hit-icon" size={15} aria-hidden="true" />
                    <span className="dash-mascot-search-hit-label">{entry.label}</span>
                    <span className="dash-mascot-search-hit-group">{entry.group}</span>
                    <IconArrowRight size={13} aria-hidden="true" />
                  </button>
                )
              })}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  )
}
